import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Search, User, LogOut, Settings, ShieldCheck, Users, UserCog, FileText, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/logo.webp";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const navigate = useNavigate();
  const { user, isAgent, isBrokerAdmin } = useAuth();

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Error signing out:", error);
        toast({
          title: "Error signing out",
          description: error.message,
          variant: "destructive",
        });
        setIsLoggingOut(false);
        return;
      }
      // Hard refresh to ensure clean state
      window.location.assign("/");
    } catch (error) {
      console.error("Unexpected error during logout:", error);
      toast({
        title: "Error signing out",
        description: "An unexpected error occurred",
        variant: "destructive",
      });
      setIsLoggingOut(false);
    }
  };

  const navLinks = [
    { name: "Home", path: "/", external: false },
    { name: "About Us", path: "/about", external: false },
    { name: "Buy", path: "https://homes.prpsj.com/idx/results/listings?idxID=b004&a_propStatus%5B%5D=ACTIVE&a_propStatus%5B%5D=COMING%2BSOON", external: true },
    { name: "Sell", path: "/sell", external: false },
    { name: "Communities", path: "/communities", external: false },
    { name: "Agents", path: "/agents", external: false },
    { name: "Careers", path: "/careers", external: false },
    { name: "Blog", path: "/blog", external: false },
    { name: "Contact", path: "/contact", external: false },
  ];

  const accountMenuItems = [
    { name: "Settings", path: "/settings", icon: Settings },
  ];

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="container flex h-16 items-center justify-between md:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img 
            src={logo} 
            alt="Prime Realty Partners logo" 
            width={160}
            height={56}
            className="h-14 md:h-16 w-auto object-contain"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.external ? (
              <a
                key={link.name}
                href={link.path}
                className="text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
              >
                {link.name}
              </a>
            ) : (
              <Link
                key={link.name}
                to={link.path}
                className="text-sm font-medium text-muted-foreground transition-colors hover:text-primary"
              >
                {link.name}
              </Link>
            )
          )}
        </nav>

        {/* Desktop Right Section */}
        <div className="hidden md:flex items-center gap-4">
          <a
            href="https://homes.prpsj.com/idx/results/listings?idxID=b004&a_propStatus%5B%5D=ACTIVE&a_propStatus%5B%5D=COMING%2BSOON"
            className="inline-flex items-center justify-center h-10 w-10 rounded-md text-muted-foreground hover:text-primary hover:bg-accent transition-colors"
          >
            <Search className="h-5 w-5" />
          </a>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2">
                  <User className="h-4 w-4" />
                  <span className="hidden lg:inline">My Account</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  {user.email}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                
                {accountMenuItems.map((item) => (
                  <DropdownMenuItem key={item.path} asChild className="cursor-pointer">
                    <Link to={item.path}>
                      <item.icon className="mr-2 h-4 w-4" />
                      {item.name}
                    </Link>
                  </DropdownMenuItem>
                ))}
                
                    {isAgent && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuLabel className="text-xs text-muted-foreground">
                          Agent Tools
                        </DropdownMenuLabel>
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <Link to="/agent">
                        <LayoutDashboard className="mr-2 h-4 w-4" />
                        Agent Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <Link to="/agent/profile/edit">
                        <UserCog className="mr-2 h-4 w-4" />
                        Edit Profile
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <Link to="/agent/open-house">
                        <Users className="mr-2 h-4 w-4" />
                        Open House
                      </Link>
                    </DropdownMenuItem>
                      </>
                    )}
                
                {isBrokerAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      Admin Portal
                    </DropdownMenuLabel>
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <Link to="/admin">
                        <ShieldCheck className="mr-2 h-4 w-4" />
                        Admin Dashboard
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <Link to="/admin/agents">
                        <UserCog className="mr-2 h-4 w-4" />
                        Agent Management
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <Link to="/admin/users">
                        <Users className="mr-2 h-4 w-4" />
                        User Management
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="cursor-pointer">
                      <Link to="/admin/blog">
                        <FileText className="mr-2 h-4 w-4" />
                        Blog Management
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
                
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    handleLogout();
                  }}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => window.location.href = "https://homes.prpsj.com/idx/userlogin"}>
                Sign In
              </Button>
              <Button size="sm" onClick={() => window.location.href = "https://homes.prpsj.com/idx/usersignup"}>
                Create Account
              </Button>
            </div>
          )}
        </div>

        {/* Mobile Menu Button */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
        >
          {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </Button>
      </div>

      {/* Mobile Menu */}
      {isMenuOpen && (
        <div className="md:hidden border-t border-border bg-background animate-fade-in">
          <nav className="container py-4 flex flex-col gap-2">
            {navLinks.map((link) =>
              link.external ? (
                <a
                  key={link.name}
                  href={link.path}
                  className="py-2 px-4 text-sm font-medium text-muted-foreground hover:text-primary hover:bg-muted rounded-md transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </a>
              ) : (
                <Link
                  key={link.name}
                  to={link.path}
                  className="py-2 px-4 text-sm font-medium text-muted-foreground hover:text-primary hover:bg-muted rounded-md transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {link.name}
                </Link>
              )
            )}
            <div className="border-t border-border my-2" />
            {user ? (
              <>
                {accountMenuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="py-2 px-4 text-sm font-medium text-muted-foreground hover:text-primary hover:bg-muted rounded-md transition-colors flex items-center gap-2"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.name}
                  </Link>
                ))}
                {isAgent && (
                  <>
                    <Link
                      to="/agent"
                      className="py-2 px-4 text-sm font-medium text-primary hover:bg-muted rounded-md transition-colors flex items-center gap-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      Agent Dashboard
                    </Link>
                    <Link
                      to="/agent/profile/edit"
                      className="py-2 px-4 text-sm font-medium text-primary hover:bg-muted rounded-md transition-colors flex items-center gap-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <UserCog className="h-4 w-4" />
                      Edit Profile
                    </Link>
                    <Link
                      to="/agent/open-house"
                      className="py-2 px-4 text-sm font-medium text-primary hover:bg-muted rounded-md transition-colors flex items-center gap-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Users className="h-4 w-4" />
                      Open House
                    </Link>
                  </>
                )}
                {isBrokerAdmin && (
                  <>
                    <Link
                      to="/admin"
                      className="py-2 px-4 text-sm font-medium text-primary hover:bg-muted rounded-md transition-colors flex items-center gap-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <ShieldCheck className="h-4 w-4" />
                      Admin Dashboard
                    </Link>
                    <Link
                      to="/admin/agents"
                      className="py-2 px-4 text-sm font-medium text-primary hover:bg-muted rounded-md transition-colors flex items-center gap-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <UserCog className="h-4 w-4" />
                      Agent Management
                    </Link>
                    <Link
                      to="/admin/users"
                      className="py-2 px-4 text-sm font-medium text-primary hover:bg-muted rounded-md transition-colors flex items-center gap-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <Users className="h-4 w-4" />
                      User Management
                    </Link>
                    <Link
                      to="/admin/blog"
                      className="py-2 px-4 text-sm font-medium text-primary hover:bg-muted rounded-md transition-colors flex items-center gap-2"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <FileText className="h-4 w-4" />
                      Blog Management
                    </Link>
                  </>
                )}
                <button
                  onClick={() => {
                    handleLogout();
                    setIsMenuOpen(false);
                  }}
                  className="py-2 px-4 text-sm font-medium text-destructive hover:bg-muted rounded-md transition-colors text-left flex items-center gap-2"
                >
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-2 px-4 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    window.location.href = "https://homes.prpsj.com/idx/userlogin";
                    setIsMenuOpen(false);
                  }}
                >
                  Sign In
                </Button>
                <Button
                  onClick={() => {
                    window.location.href = "https://homes.prpsj.com/idx/usersignup";
                    setIsMenuOpen(false);
                  }}
                >
                  Create Account
                </Button>
              </div>
            )}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
